import type React from 'react';

import { ROLE_OPTIONS } from '@vlabs/api-bindings/src/constants';
import {
  ActivityIcon,
  VideoOnIcon,
  NotificationOnIcon,
  TechServerIcon,
  DetectionIcon,
  UserCardIcon,
  HistoryIcon,
  VideoTvIcon,
  SearchIcon,
  ChartPieIcon,
  BarProgressIcon,
  KeyIcon,
  ListIcon,
  UsersIcon,
} from '@vlabs/icons';
import { VIDEO_ANALYTIC_TYPES } from '@vlabs/monokit/shared/lib/video-analytics/constants';
import {
  LanguageButton, NavbarPanel, NavbarPanelFooter, NavbarPanelHeader,
  NavbarPanelSection, NavbarPanelViewer, NavbarTypeToggleButton, ThemeButton,
  ViewerAuthButton, NavbarGroup, NavbarItem, defaultLanguageOptions,
} from '@vlabs/monokit/shared/ui/navbar-panel';
import { WhiteLabelBrand } from '@vlabs/monokit/shared/ui/white-label-brand';
import { NavbarLinkWithPermissions } from '@vlabs/shared/components/navbar-link-with-permissions/NavbarLinkWithPermissions';
import { permissions } from '@vlabs/shared/config';
import { Badge } from '@vlabs/uikit';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';

import { selectAccountModel, selectAccountRole, selectIsAuthenticated } from '@vlabs/pages/auth/selectors';
import { logout } from '@vlabs/pages/auth/store';
import { selectNotifyEvents } from '@vlabs/pages/point/workspace/selectors';
import {
  selectAppConfig,
  selectAppServiceState,
  selectIsAppFeatureAvailable,
} from '@vlapps/point/src/app/store/selectors';

import { routes } from '../routes';
import { useNavigationTranslation } from './i18n';

// Опции языков формируются из build-time флагов локалей (__LOCALE_*):
// берём из defaultLanguageOptions только те локали, что включены в сборку.
const languageOptions = [
  ...(__LOCALE_RU__ ? defaultLanguageOptions.filter((option) => option.value === 'ru') : []),
  ...(__LOCALE_EN__ ? defaultLanguageOptions.filter((option) => option.value === 'en') : []),
  ...(__LOCALE_PTBR__ ? defaultLanguageOptions.filter((option) => option.value === 'pt-BR') : []),
  ...(__LOCALE_ESES__ ? defaultLanguageOptions.filter((option) => option.value === 'es') : []),
];

function PointNavbar(): React.JSX.Element {
  const { t } = useNavigationTranslation();
  const { pathname } = useLocation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const account = useSelector(selectAccountModel);
  const role = useSelector(selectAccountRole) ?? ROLE_OPTIONS[0];
  const notifyEvents = useSelector(selectNotifyEvents);
  const { uiNotificationsEnabled } = useSelector(selectAppConfig);
  const lunaPlatformIsAvailable = useSelector((state) => selectAppServiceState(state, 'lunaPlatform')) === 'loaded';
  const lunaStreamsIsAvailable = useSelector((state) => selectAppServiceState(state, 'lunaStreams')) === 'loaded';
  const clementineAuthIsAvailable = useSelector((state) => selectAppServiceState(state, 'clementineAuth')) === 'loaded';
  const notifierIsAvailable = useSelector((state) => selectAppServiceState(state, 'vlNotifier')) === 'loaded';
  const videoAnalyticsIsAvailable = useSelector((state) => selectIsAppFeatureAvailable(state, 'components.video_analytics'));
  const dispatch = useDispatch();

  const hasAlarmTag = notifyEvents.some((event) => (
    VIDEO_ANALYTIC_TYPES.includes(event.event_type)
      || event.tags?.includes('alarm')
  ));

  return (
    <NavbarPanel
      footer={(
        <NavbarPanelFooter
          viewer={(
            <NavbarPanelViewer
              name={account?.name}
              role={ROLE_OPTIONS[role]?.label}
            />
          )}
        >
          <ThemeButton />
          <LanguageButton languageOptions={languageOptions} />
          <ViewerAuthButton
            isAuthenticated={isAuthenticated}
            onLogout={() => {
              logout(dispatch);
            }}
          />
        </NavbarPanelFooter>
      )}
      header={(
        <NavbarPanelHeader
          logo={<WhiteLabelBrand />}
          toggleButton={<NavbarTypeToggleButton />}
        />
      )}
      sections={[
        <NavbarPanelSection
          key="events"
          name={t('events_and_video')}
        >
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              badge={hasAlarmTag ? <Badge shape="dot" /> : undefined}
              data-testid="workspace"
              exact
              icon={<ActivityIcon />}
              name={t('workspace')}
              permissions={{ rules: [permissions.event.view] }}
              to={routes.events.latest}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="fs_event_archive"
              icon={<HistoryIcon />}
              name={t('fs_event_archive')}
              permissions={{ rules: [permissions.event.view] }}
              to={routes.events.archive}
            />
          )}
          {!!lunaPlatformIsAvailable && !!videoAnalyticsIsAvailable && (
            <NavbarItem
              data-testid="va.archive"
              icon={<ListIcon />}
              name={t('va_event_archive')}
              to={routes.videoAnalytics.archive}
            />
          )}
          {!!lunaStreamsIsAvailable && (
            <NavbarItem
              data-testid="videowall"
              icon={<VideoTvIcon />}
              name={t('videowall')}
              to={routes.videoWall.index}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="search"
              icon={<SearchIcon />}
              name={t('search')}
              permissions={{ rules: [permissions.event.matching, permissions.face.matching] }}
              to={routes.search}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarItem
              data-testid="statistic"
              icon={<ChartPieIcon />}
              name={t('fs_event_statistic')}
              to={routes.events.statistic.all}
            />
          )}
        </NavbarPanelSection>,
        <NavbarPanelSection
          key="preferences"
          name={t('settings_and_catalogs')}
        >
          {!!lunaPlatformIsAvailable && (
            <NavbarItem
              data-testid="cards_and_lists"
              icon={<UserCardIcon />}
              name={t('cards_and_lists')}
              to={routes.cardsAndLists.index}
            />
          )}
          <NavbarGroup
            active={
              !!matchPath(pathname, { path: routes.sources.main })
              || !!matchPath(pathname, { path: routes.videoAnalytics.all })
              || !!matchPath(pathname, { path: routes.sources.vms.main })
            }
            icon={<VideoOnIcon />}
            name={t('sources')}
          >
            {!!lunaPlatformIsAvailable && (
              <NavbarItem
                data-testid="fs_sources"
                icon={<VideoOnIcon />}
                name={t('fs_sources')}
                to={routes.sources.main}
              />
            )}
            {!!lunaPlatformIsAvailable && (
              <NavbarLinkWithPermissions
                data-testid="va_sources"
                name={t('va_sources')}
                permissions={{ rules: [permissions.videoanalytics.view] }}
                to={routes.videoAnalytics.all}
              />
            )}
            {!!lunaStreamsIsAvailable && (
              <NavbarItem
                data-testid="vms"
                name={t('vms')}
                to={routes.sources.vms.main}
              />
            )}
          </NavbarGroup>
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="handlers"
              icon={<DetectionIcon />}
              name={t('handlers')}
              permissions={{ rules: [permissions.handler.view] }}
              to={routes.handlers.all}
            />
          )}
        </NavbarPanelSection>,
        <NavbarPanelSection
          key="administration"
          name={t('administration')}
        >
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="tasks"
              icon={<BarProgressIcon />}
              name={t('tasks')}
              permissions={{ rules: [permissions.task.view] }}
              to={routes.tasks.all}
            />
          )}
          {!!clementineAuthIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="accounts"
              icon={<UsersIcon />}
              name={t('accounts')}
              permissions={{ rules: [permissions.account.view] }}
              to={routes.accounts.all}
            />
          )}
          <NavbarLinkWithPermissions
            data-testid="monitoring"
            icon={<TechServerIcon />}
            name={t('monitoring')}
            permissions={{ rules: [permissions.appInfo.view] }}
            to={routes.app.info}
          />
          <NavbarLinkWithPermissions
            data-testid="features"
            icon={<KeyIcon />}
            name={t('features')}
            permissions={{ rules: [permissions.appInfo.view] }}
            to={routes.app.features}
          />
          {!!notifierIsAvailable && !!uiNotificationsEnabled && (
            <NavbarLinkWithPermissions
              data-testid="notifications"
              icon={<NotificationOnIcon />}
              name={t('notifications')}
              permissions={{ rules: [permissions.notification.view] }}
              to={routes.notifications.all}
            />
          )}
        </NavbarPanelSection>,
      ]}
    />
  );
}

export { PointNavbar };
